import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { usePwaInstall } from '../lib/usePwaInstall'
import './ConfiguracionPaciente.css'

export default function ConfiguracionPaciente({ usuario, onIrAPerfil, onIrAInicio, onIrAHabitos, onIrARegistrarProgreso }) {
  // Ronda 2 (#2): "Instalar app" se mueve de Perfil > Cuenta a este hub,
  // como un ítem más de la checklist — mismo criterio que el resto.
  const { disponible: instalacionDisponible, instalar: instalarApp } = usePwaInstall()
  const appInstalada = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  const [loading, setLoading] = useState(true)
  const [fotoInspiracionalHecha, setFotoInspiracionalHecha] = useState(false)
  const [frasesPropias, setFrasesPropias] = useState([])
  const [habitosActivos, setHabitosActivos] = useState(0)
  const [progresoHecho, setProgresoHecho] = useState(false)
  const [suplementosAsignados, setSuplementosAsignados] = useState(0)
  const [suplementosActivados, setSuplementosActivados] = useState(0)
  const [nuevaFrase, setNuevaFrase] = useState('')
  const [guardandoFrase, setGuardandoFrase] = useState(false)

  useEffect(() => { cargarDatos() }, [usuario.id])

  const cargarDatos = async () => {
    setLoading(true)
    const [{ data: fotos }, { data: frases }, { data: habitos }, { data: progreso }, { data: suplementos }] = await Promise.all([
      supabase.from('fotos_inspiracionales').select('usuario_id').eq('usuario_id', usuario.id).limit(1),
      supabase.from('frases_motivacionales').select('*').eq('usuario_id', usuario.id).eq('tipo', 'usuario'),
      supabase.from('habitos_config').select('id').eq('paciente_id', usuario.id).eq('activo', true).limit(1),
      supabase.from('registros_progreso').select('id').eq('usuario_id', usuario.id).limit(1),
      supabase.from('suplementos_config').select('id, activo_paciente').eq('paciente_id', usuario.id)
    ])
    setFotoInspiracionalHecha((fotos || []).length > 0)
    setFrasesPropias(frases || [])
    setHabitosActivos((habitos || []).length)
    setProgresoHecho((progreso || []).length > 0)
    setSuplementosAsignados((suplementos || []).length)
    setSuplementosActivados((suplementos || []).filter(s => s.activo_paciente).length)
    setLoading(false)
  }

  const guardarFrase = async () => {
    if (!nuevaFrase.trim()) return
    setGuardandoFrase(true)
    const { data, error } = await supabase.from('frases_motivacionales').insert({
      texto: nuevaFrase.trim(),
      tipo: 'usuario',
      usuario_id: usuario.id,
      activa: true
    }).select().single()
    setGuardandoFrase(false)
    if (error) { alert('No se pudo guardar la frase. Probá de nuevo.'); return }
    if (data) setFrasesPropias(prev => [...prev, data])
    setNuevaFrase('')
  }

  if (loading) return <div className="loading">Cargando...</div>

  const fotoPerfilHecha = !!usuario?.user_metadata?.foto_perfil_url

  const items = [
    { id: 'foto_perfil', label: 'Foto de perfil', hecho: fotoPerfilHecha, onClick: onIrAPerfil },
    { id: 'foto_inspiracional', label: 'Foto inspiracional', hecho: fotoInspiracionalHecha, onClick: onIrAInicio },
    { id: 'frases', label: 'Frases propias', hecho: frasesPropias.length > 0, onClick: null },
    { id: 'habitos', label: 'Activar hábitos', hecho: habitosActivos > 0, onClick: onIrAHabitos },
    { id: 'progreso', label: 'Registro inicial de Progreso', hecho: progresoHecho, onClick: onIrARegistrarProgreso },
    // Solo aparece si la nutri le asignó al menos un suplemento — a
    // diferencia de Hábitos (catálogo abierto), acá no hay nada para
    // activar si todavía no le asignaron nada.
    ...(suplementosAsignados > 0
      ? [{ id: 'suplementos', label: 'Activar suplementos', hecho: suplementosActivados > 0, onClick: onIrAPerfil }]
      : []),
    // Sin onClick si ya está instalada o si el navegador no ofreció el
    // prompt (iOS, o ya se instaló por otra vía) — mismo "sin-click" que
    // Frases, para no dejar un ítem clickeable que no hace nada.
    { id: 'instalar_app', label: 'Instalar app', hecho: appInstalada, onClick: !appInstalada && instalacionDisponible ? instalarApp : null }
  ]
  const hechos = items.filter(i => i.hecho).length
  const porcentaje = Math.round((hechos / items.length) * 100)

  return (
    <div className="config-paciente-page">
      <div className="page-header">
        <h1 className="page-titulo">Personalizá tu espacio</h1>
        <p className="page-subtitulo">Terminá de armar tu espacio</p>
      </div>

      <div className="config-paciente-contenido">
        <div className="config-paciente-avance">
          <div className="config-paciente-avance-header">
            <span className="config-paciente-avance-porcentaje">{porcentaje}%</span>
            <span className="config-paciente-avance-label">{hechos} de {items.length} completados</span>
          </div>
          <div className="config-paciente-barra-fondo">
            <div className="config-paciente-barra-fill" style={{ width: `${porcentaje}%` }} />
          </div>
        </div>

        <div className="config-paciente-lista">
          {items.map(item => (
            <button
              key={item.id}
              className={`config-paciente-item ${item.hecho ? 'hecho' : ''} ${item.onClick ? '' : 'sin-click'}`}
              onClick={item.onClick || undefined}
              disabled={!item.onClick}
            >
              <span className={`config-paciente-check ${item.hecho ? 'checked' : ''}`}>
                {item.hecho && '✓'}
              </span>
              <span className="config-paciente-item-label">{item.label}</span>
              {item.onClick && <span className="config-paciente-item-arrow">›</span>}
            </button>
          ))}
        </div>

        {/* FRASES — único ítem que sigue abierto para cargar contenido nuevo
            con el tiempo, aunque el checklist ya esté al 100%. */}
        <div className="config-paciente-frases">
          <p className="seccion-titulo">✍️ Tus frases ({frasesPropias.length})</p>
          {frasesPropias.length > 0 && (
            <div className="config-paciente-frases-lista">
              {frasesPropias.map(f => (
                <p key={f.id} className="config-paciente-frase-item">"{f.texto}"</p>
              ))}
            </div>
          )}
          <textarea
            className="modal-textarea"
            placeholder="Escribí una frase que te inspire..."
            value={nuevaFrase}
            onChange={e => setNuevaFrase(e.target.value)}
            rows={3}
          />
          <button className="btn-primario" onClick={guardarFrase} disabled={guardandoFrase || !nuevaFrase.trim()}>
            {guardandoFrase ? 'Guardando...' : '+ Agregar frase'}
          </button>
        </div>
      </div>
    </div>
  )
}
